import { useContext, useEffect, useMemo, useRef, useState } from "react";
import AuthContext from "../context/AuthContext";

const OPEN = 1;

export function useChatThread(wsRef, status, onNewMessage, { threadId } = {}) {
  const { token } = useContext(AuthContext);
  const subRef = useRef(false);

  // sub / unsub
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !threadId || status !== "connected") return;
    ws.send(JSON.stringify({ type: "chat.subscribe", thread_id: threadId }));
    subRef.current = true;

    return () => {
      if (ws.readyState === OPEN) {
        ws.send(JSON.stringify({ type: "unsubscribe", topic: `chat-${threadId}` }));
      }
      subRef.current = false;
    };
  }, [wsRef, status, threadId]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;

    const onMessage = (evt) => {
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (msg.type !== "chat.message") return;

      const m = msg.payload || msg;

      if (m.thread_id != threadId) return;

      onNewMessage(m);
    };

    ws.addEventListener("message", onMessage);
    return () => ws.removeEventListener("message", onMessage);
  }, [wsRef, threadId]);

  // sender

  // `options.messageType` ("text" | "image" | "video") plus `options.imageAssetId` /
  // `options.videoAssetId` allow sending media (optionally with a caption) messages
  // through the same chat.send WS path used for plain text messages.
  const sendMessage = useMemo(
    () =>
      (text, options = {}) => {
        const ws = wsRef.current;
        const { messageType = "text", imageAssetId = null, mediaAssetId = null, videoAssetId = null } = options;
        const t = (text || "").trim();

        if (!ws || ws.readyState !== OPEN || !threadId) return false;

        if (messageType === "image") {
          if (!mediaAssetId && !imageAssetId) return false;
        } else if (messageType === "video") {
          if (!videoAssetId) return false;
        } else if (!t) {
          return false;
        }

        ws.send(
          JSON.stringify({
            type: "chat.send",
            thread_id: threadId,
            text: t,
            message_type: messageType,
            ...(mediaAssetId ? { media_asset_id: mediaAssetId } : imageAssetId ? { image_asset_id: imageAssetId } : {}),
            ...(videoAssetId ? { video_asset_id: videoAssetId } : {}),
          })
        );
        return true;
      },
    [wsRef, threadId]
  );

  return { sendMessage, isSubscribed: subRef.current };
}
